import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { buildMonthGrid, WOCHENTAGE_KURZ, monatsLabel, todayIso, formatZeit } from '../utils/dienstplanKalender';

const MAX_ANWESENHEIT_MINUTEN = 10 * 60 + 45;

const zeitZuMinuten = (zeit) => {
  const [h, m] = zeit.split(':').map(Number);
  return h * 60 + m;
};

// Gemeinsamer Schichtkalender für die öffentliche Dienstplan-Seite (Token+PIN)
// und das MA-Portal (Session). Ohne canEdit nur Tagesmarker aus tageMap.
// onAnmelden(schicht, von, bis) / onAbmelden(schicht) geben true bei Erfolg zurück.
export default function DienstplanKalender({
  schichten,
  meineSchichten,
  tageMap = {},
  canEdit,
  lockedHinweis = 'Details sind erst nach Freischaltung sichtbar.',
  submitting,
  onAnmelden,
  onAbmelden,
}) {
  const now = new Date();
  const [anzeige, setAnzeige] = useState({ year: now.getFullYear(), month: now.getMonth() });
  const [dialogDatum, setDialogDatum] = useState(null);
  const [hinweis, setHinweis] = useState('');
  // { schichtId, von, bis } – offenes Zeit-Formular beim Eintragen
  const [eintrag, setEintrag] = useState(null);

  const heute = todayIso();

  const meineMap = useMemo(() => {
    const map = new Map();
    (meineSchichten || []).forEach((m) => map.set(m.schicht_id, m));
    return map;
  }, [meineSchichten]);

  const schichtenByDatum = useMemo(() => {
    const map = {};
    (schichten || []).forEach((s) => {
      if (!map[s.datum]) map[s.datum] = [];
      map[s.datum].push(s);
    });
    return map;
  }, [schichten]);

  const closeDialog = () => {
    setDialogDatum(null);
    setEintrag(null);
    setHinweis('');
  };

  const handleAnmelden = async (schicht) => {
    if (submitting || !eintrag) return;
    const von = eintrag.von;
    const bis = eintrag.bis || null;
    if (!/^\d{2}:\d{2}$/.test(von)) {
      setHinweis('Bitte eine Startzeit angeben.');
      return;
    }
    if (bis && zeitZuMinuten(bis) <= zeitZuMinuten(von)) {
      setHinweis('Ende muss nach Beginn liegen.');
      return;
    }
    if (schicht.zeit_von && zeitZuMinuten(von) < zeitZuMinuten(schicht.zeit_von)) {
      setHinweis(`Beginn darf nicht vor Schichtbeginn (${schicht.zeit_von} Uhr) liegen.`);
      return;
    }
    if (schicht.zeit_bis && (zeitZuMinuten(von) >= zeitZuMinuten(schicht.zeit_bis) || (bis && zeitZuMinuten(bis) > zeitZuMinuten(schicht.zeit_bis)))) {
      setHinweis(`Die Zeit muss innerhalb der Schichtzeit (bis ${schicht.zeit_bis} Uhr) liegen.`);
      return;
    }
    if (bis && zeitZuMinuten(bis) - zeitZuMinuten(von) > MAX_ANWESENHEIT_MINUTEN) {
      setHinweis('Maximale Anwesenheit pro Tag: 10,75 Std. (10 Std. Arbeit + 45 Min. Pause).');
      return;
    }
    setHinweis('');
    const ok = await onAnmelden(schicht, von, bis);
    if (ok) setEintrag(null);
  };

  const handleAbmelden = async (schicht) => {
    if (submitting) return;
    setHinweis('');
    await onAbmelden(schicht);
  };

  const wechsleMonat = (delta) => {
    setAnzeige((prev) => {
      const d = new Date(prev.year, prev.month + delta, 1);
      return { year: d.getFullYear(), month: d.getMonth() };
    });
  };

  const weeks = buildMonthGrid(anzeige.year, anzeige.month);

  return (
    <>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <IconButton onClick={() => wechsleMonat(-1)} aria-label="Vorheriger Monat"><ChevronLeft /></IconButton>
        <Typography variant="h6">{monatsLabel(anzeige.year, anzeige.month)}</Typography>
        <IconButton onClick={() => wechsleMonat(1)} aria-label="Nächster Monat"><ChevronRight /></IconButton>
      </Stack>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
        {WOCHENTAGE_KURZ.map((wt) => (
          <Typography key={wt} variant="caption" sx={{ textAlign: 'center', fontWeight: 700, color: 'text.secondary' }}>{wt}</Typography>
        ))}
        {weeks.flat().map((cell, idx) => {
          if (!cell) return <Box key={`leer-${idx}`} />;
          const tagSchichten = schichtenByDatum[cell.iso] || [];
          const anzahl = canEdit ? tagSchichten.length : (tageMap[cell.iso] || 0);
          const hatSchichten = anzahl > 0;
          const vergangen = cell.iso < heute;
          const angemeldet = tagSchichten.some((s) => meineMap.has(s.schicht_id));
          const voll = canEdit && hatSchichten && tagSchichten.every((s) => s.max_plaetze !== null && s.angemeldet >= s.max_plaetze);
          const klickbar = hatSchichten && !vergangen;

          return (
            <Box
              key={cell.iso}
              onClick={() => klickbar && setDialogDatum(cell.iso)}
              sx={{
                aspectRatio: '1 / 1',
                borderRadius: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.25,
                border: '1px solid',
                borderColor: hatSchichten ? (angemeldet ? 'success.main' : 'primary.main') : 'transparent',
                bgcolor: !hatSchichten ? 'transparent'
                  : vergangen ? 'grey.200'
                  : angemeldet ? 'success.light'
                  : voll ? 'grey.300'
                  : 'primary.50',
                opacity: vergangen ? 0.5 : 1,
                cursor: klickbar ? 'pointer' : 'default',
                '&:hover': klickbar ? { boxShadow: 2 } : {}
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: hatSchichten ? 700 : 400, color: cell.iso === heute ? 'primary.main' : 'text.primary' }}>
                {cell.day}
              </Typography>
              {hatSchichten ? (
                <Typography variant="caption" sx={{ lineHeight: 1 }}>
                  {!canEdit
                    ? (anzahl > 1 ? `${anzahl} Schichten` : 'geplant')
                    : anzahl > 1
                    ? `${anzahl} Schichten`
                    : `${tagSchichten[0].angemeldet}${tagSchichten[0].max_plaetze !== null ? `/${tagSchichten[0].max_plaetze}` : ''}`}
                </Typography>
              ) : null}
            </Box>
          );
        })}
      </Box>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Chip size="small" sx={{ bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.main' }} label="freigeschaltet" />
        <Chip size="small" sx={{ bgcolor: 'success.light', border: '1px solid', borderColor: 'success.main' }} label="du bist eingetragen" />
        <Chip size="small" sx={{ bgcolor: 'grey.300' }} label="voll belegt" />
      </Stack>

      <Dialog open={Boolean(dialogDatum)} onClose={closeDialog} maxWidth="xs" fullWidth>
        <DialogTitle>
          {dialogDatum ? new Date(`${dialogDatum}T00:00:00`).toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' }) : ''}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            {!canEdit ? (
              <Alert severity="info">
                {(tageMap[dialogDatum] || 0) > 1
                  ? `An diesem Tag sind ${tageMap[dialogDatum]} Schichten geplant.`
                  : 'An diesem Tag ist eine Schicht geplant.'}{' '}
                {lockedHinweis}
              </Alert>
            ) : null}
            {hinweis ? <Alert severity="warning" onClose={() => setHinweis('')}>{hinweis}</Alert> : null}
            {canEdit ? (schichtenByDatum[dialogDatum] || []).map((s) => {
              const meine = meineMap.get(s.schicht_id);
              const voll = s.max_plaetze !== null && s.angemeldet >= s.max_plaetze;
              const zeitVorgabe = s.zeit_von || s.zeit_bis
                ? [
                    s.zeit_von ? `Frühester Beginn: ${s.zeit_von} Uhr` : 'Beginn: keine Vorgabe',
                    s.zeit_bis ? `Spätestes Ende: ${s.zeit_bis} Uhr` : 'Ende: keine Vorgabe',
                  ].join(' · ')
                : null;
              const formOffen = eintrag?.schichtId === s.schicht_id;
              return (
                <Card key={s.schicht_id} variant="outlined" sx={{ bgcolor: meine ? 'success.light' : voll ? 'grey.100' : 'background.paper' }}>
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{s.ort}</Typography>
                        <Typography variant="body2" color={zeitVorgabe ? 'text.secondary' : 'text.disabled'}>
                          {zeitVorgabe || 'Noch keine Zeitvorgabe für diese Schicht'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {s.angemeldet}{s.max_plaetze !== null ? ` von ${s.max_plaetze}` : ''} angemeldet
                        </Typography>
                        {s.notiz ? <Typography variant="body2" color="text.secondary">{s.notiz}</Typography> : null}
                        {meine ? (
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            Du: {meine.zeit_von ? formatZeit(meine) : 'eingetragen'}
                          </Typography>
                        ) : null}
                      </Box>
                      {meine ? (
                        <Button size="small" variant="outlined" color="error" disabled={submitting} onClick={() => handleAbmelden(s)}>
                          Austragen
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          variant="contained"
                          disabled={submitting || voll || formOffen}
                          onClick={() => { setHinweis(''); setEintrag({ schichtId: s.schicht_id, von: s.zeit_von || '', bis: s.zeit_bis || '' }); }}
                        >
                          {voll ? 'Voll' : 'Eintragen'}
                        </Button>
                      )}
                    </Stack>
                    {formOffen ? (
                      <Stack spacing={1} sx={{ mt: 1.5 }}>
                        <Stack direction="row" spacing={1}>
                          <TextField
                            label="Dein Beginn"
                            type="time"
                            size="small"
                            value={eintrag.von}
                            onChange={(e) => setEintrag((prev) => ({ ...prev, von: e.target.value }))}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            required
                          />
                          <TextField
                            label="Dein Ende (optional)"
                            type="time"
                            size="small"
                            value={eintrag.bis}
                            onChange={(e) => setEintrag((prev) => ({ ...prev, bis: e.target.value }))}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                          />
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          Hinweis: Ab 6 Std. Arbeitszeit werden 30 Min. Pause veranschlagt, ab 9 Std. insgesamt 45 Min.
                          Maximale Anwesenheit pro Tag: 10,75 Std. (10 Std. Arbeit + 45 Min. Pause).
                        </Typography>
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button size="small" onClick={() => { setEintrag(null); setHinweis(''); }} disabled={submitting}>Abbrechen</Button>
                          <Button size="small" variant="contained" onClick={() => handleAnmelden(s)} disabled={submitting}>Bestätigen</Button>
                        </Stack>
                      </Stack>
                    ) : null}
                  </CardContent>
                </Card>
              );
            }) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Schließen</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
